import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bolt, RefreshCw } from "lucide-react";
import { seguimientoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { ApplicationsTable, type Column } from "../applications/shared/ApplicationsTable";
import { Badge } from "./ui";
import { ACTIVITY_LABELS, ACTIVITY_TONE } from "./labels";
import type { ActivoRow } from "./types";

type Row = ActivoRow & { id: number };

const TYPE_FILTERS = [
  { value: "", label: "Todos" },
  { value: "producer", label: "Productores" },
  { value: "artist", label: "Artistas" },
  { value: "provider", label: "Proveedores" },
];

function lastSeenLabel(row: ActivoRow): string {
  if (row.lastSeenAt == null || row.daysSinceActivity == null) return "Nunca entró";
  if (row.daysSinceActivity <= 0) return "Hoy";
  return `Hace ${row.daysSinceActivity} d`;
}

export function ActivosPane() {
  const [type, setType] = useState("");

  const listQuery = useQuery({
    queryKey: ["seg", "activos", { type }],
    queryFn: () => seguimientoApi.activos(type ? { type } : undefined),
  });

  const rows: Row[] = (listQuery.data ?? []).map((r) => ({ ...r, id: r.userId }));

  const columns: Column<Row>[] = [
    {
      key: "name",
      header: "Nombre",
      cell: (r) => (
        <div>
          <div className="font-medium text-fg">{r.name}</div>
          <div className="text-2xs text-fg-subtle break-all">{r.email}</div>
        </div>
      ),
    },
    {
      key: "roles",
      header: "Roles",
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.isProducer && <Badge tone="accent">Productor</Badge>}
          {r.isArtist && <Badge tone="info">Artista</Badge>}
          {r.isPartner && <Badge tone="success">Proveedor</Badge>}
        </div>
      ),
    },
    {
      key: "activity",
      header: "Actividad",
      cell: (r) => <Badge tone={ACTIVITY_TONE[r.activityStatus]}>{ACTIVITY_LABELS[r.activityStatus]}</Badge>,
      width: "min",
    },
    {
      key: "lastSeen",
      header: "Últ. actividad",
      cell: (r) => <span className="text-xs text-fg-muted">{lastSeenLabel(r)}</span>,
      width: "min",
    },
    {
      key: "responsable",
      header: "Responsable",
      cell: (r) =>
        r.prospect?.responsable ? (
          <span className="text-xs text-fg">{r.prospect.responsable.name}</span>
        ) : (
          <span className="text-2xs text-fg-subtle">—</span>
        ),
      width: "min",
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="nx-input w-auto"
        >
          {TYPE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-2xs text-fg-subtle inline-flex items-center gap-1">
          <Bolt size={12} /> En vivo desde el sistema — actividad y estado de cuenta no se tipean.
        </span>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary ml-auto"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando activos…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<Row>
          rows={rows}
          columns={columns}
          page={1}
          pageSize={rows.length || 1}
          total={rows.length}
          onPageChange={() => {}}
          emptyLabel="No hay usuarios activos con ese filtro."
        />
      )}
    </div>
  );
}
